import { useCallback, useEffect, useRef, useState } from 'react';
import { getBalance } from '@/services/activity/activity';

export const useBalance = (
    defaultBalance: number,
    userExist: boolean
) => {
    const [balance, setBalance] = useState<number>(defaultBalance);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const timeSleep = Number(import.meta.env.VITE_BALANCE_REFRESH_INTERVAL || 300000);
    const maxRetries = 3; // Número máximo de reintentos
    const [countError, setCountError] = useState(0);

    const fetchBalanceWithRetries = useCallback(async () => {
        if (loading) {
            return;
        }
        try {
            setLoading(true);
            const response = await getBalance();
            setBalance(response?.balances[0]?.balance || 0);
            setError(null);
        } catch (err) {
            console.error('Error fetching balance:', err);
            setCountError(countError + 1);
        } finally {
            setLoading(false);
        }
    }, [countError, loading]);

    const init = useCallback(async () => {
        if (countError > maxRetries) {
            setError('Failed to fetch balance after multiple attempts');
            return;
        }
        setTimeout(async () => {
            await fetchBalanceWithRetries();
            await init();
        }, timeSleep);
    }, [countError, maxRetries, timeSleep, fetchBalanceWithRetries]);

    const firstRender = useRef(true);
    useEffect(() => {
        if (!firstRender.current || defaultBalance > 0 || !userExist) {
            return;
        }
        firstRender.current = false;
        const fetchData = async () => {
            await fetchBalanceWithRetries();
            await init();
        };
        fetchData();
    }, [init, defaultBalance, userExist, fetchBalanceWithRetries]);

    return { balance, loading, error, refetch: fetchBalanceWithRetries };
};
